import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getCRMLeads, createCRMLead, updateCRMLead, deleteCRMLead,
  getCRMDeals, createCRMDeal, updateCRMDeal, deleteCRMDeal,
  getCRMTasks, createCRMTask, updateCRMTask, deleteCRMTask,
  getCRMStats, createCRMActivity, getCRMLead,
} from '../services/api';
import {
  showSuccessToast, showErrorToast,
  showLoadingToast, updateLoadingToastSuccess, updateLoadingToastError,
} from '../utils/toastConfig';
import {
  FiArrowLeft, FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiEye,
  FiUser, FiBriefcase, FiPhone, FiMail, FiTrendingUp, FiTarget,
  FiCheckCircle, FiClock, FiAlertCircle, FiBarChart2, FiActivity,
  FiCalendar, FiFlag, FiMessageSquare, FiList, FiLayers,
  FiCheck, FiFilter, FiLogOut,
  FiChevronRight, FiChevronLeft,
} from 'react-icons/fi';
import { MdCurrencyRupee } from 'react-icons/md';
import './CRM.css';

// ── Constants ──────────────────────────────────────────
const LEAD_STATUSES = ['New', 'Contacted', 'Qualified', 'Converted', 'Lost'];
const LEAD_SOURCES  = ['Web', 'Email', 'Manual', 'Referral', 'Social Media'];
const DEAL_STAGES   = ['Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
const PRIORITIES    = ['High', 'Medium', 'Low'];
const TASK_STATUSES = ['Pending', 'In Progress', 'Completed'];
const ACTIVITY_TYPES = ['Call', 'Meeting', 'Email', 'Note', 'Follow-up'];

const STAGE_PROBABILITY = {
  Prospecting: 10, Qualification: 25, Proposal: 50,
  Negotiation: 75, 'Closed Won': 100, 'Closed Lost': 0,
};

// Maps status → CSS badge class name
const STATUS_BADGE = {
  New:        'crm-badge-new',
  Contacted:  'crm-badge-contacted',
  Qualified:  'crm-badge-qualified',
  Converted:  'crm-badge-converted',
  Lost:       'crm-badge-lost',
};

const PRIORITY_BADGE = {
  High:   'crm-badge-high',
  Medium: 'crm-badge-medium',
  Low:    'crm-badge-low',
};

const TASK_STATUS_BADGE = {
  Pending:       'crm-badge-pending',
  'In Progress': 'crm-badge-in-progress',
  Completed:     'crm-badge-completed',
};

const ACTIVITY_ICON = {
  Call:      FiPhone,
  Meeting:   FiCalendar,
  Email:     FiMail,
  Note:      FiMessageSquare,
  'Follow-up': FiFlag,
};

// Avatar color palette (Veramasa blue palette)
const AVATAR_COLORS = [
  '#0178BD', '#015a8e', '#014f7a', '#0ea5e9', '#0891b2',
  '#0284c7', '#10b981', '#14b8a6', '#f59e0b', '#ef4444',
];
const getAvatarColor = (name) => {
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// ── Helpers ──────────────────────────────────────────
const fmtINR = (v) => {
  const n = parseFloat(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const isOverdue = (due_date, status) => {
  if (!due_date || status === 'Completed') return false;
  return new Date(due_date) < new Date(new Date().toDateString());
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const timeAgo = (dateStr) => {
  const now = new Date();
  const past = new Date(dateStr);
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return fmtDate(dateStr);
};

// ── Score Badge ───────────────────────────────────────
const ScoreBadge = ({ score }) => {
  const s = parseInt(score) || 0;
  const color = s >= 75 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444';
  const bg    = s >= 75 ? '#ecfdf5' : s >= 50 ? '#fffbeb' : '#fef2f2';
  return (
    <span className="crm-score" style={{ background: bg, color }}>
      <span className="crm-score-dot" style={{ background: color }} />
      {s}
    </span>
  );
};

// ── Badge ─────────────────────────────────────────────
const Badge = ({ text, badgeMap }) => {
  const cls = badgeMap?.[text] || '';
  return (
    <span className={`crm-badge ${cls}`}>
      {text}
    </span>
  );
};

// ── Empty State ────────────────────────────────────────
const EmptyState = ({ icon: Icon, title, subtitle, onAction, actionLabel }) => (
  <div className="crm-empty">
    <div className="crm-empty-icon"><Icon size={32} /></div>
    <h4>{title}</h4>
    <p>{subtitle}</p>
    {onAction && (
      <button className="crm-btn-primary" onClick={onAction}>
        <FiPlus size={14} /> {actionLabel}
      </button>
    )}
  </div>
);

// ── SVG Donut Chart ───────────────────────────────────
const DonutChart = ({ segments, total, label }) => {
  const size = 160;
  const strokeWidth = 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="crm-donut-wrap">
      <svg className="crm-donut" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {segments.map(({ value, color, label: segLabel }, i) => {
          const pct = total > 0 ? value / total : 0;
          const dashLen = pct * circumference;
          const dashOff = -offset;
          offset += dashLen;
          return (
            <circle
              key={segLabel || i}
              cx={size / 2} cy={size / 2} r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={dashOff}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
        })}
        <text x="50%" y="48%" textAnchor="middle" className="crm-donut-total"
          style={{ fontSize: '22px', fontWeight: 800, fill: '#0f172a' }}>
          {total}
        </text>
        <text x="50%" y="62%" textAnchor="middle" className="crm-donut-label"
          style={{ fontSize: '10px', fontWeight: 600, fill: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          {label}
        </text>
      </svg>
      <div className="crm-donut-legend">
        {segments.map(({ value, color, label: segLabel }) => (
          <div key={segLabel} className="crm-legend-row">
            <span className="crm-legend-left">
              <span className="crm-legend-dot" style={{ background: color }} />
              {segLabel}
            </span>
            <span className="crm-legend-value">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
//  MAIN CRM COMPONENT
// ══════════════════════════════════════════════════════
const CRM = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // ── Active Tab ──
  const [activeTab, setActiveTab] = useState('leads');

  // ── Data ──
  const [leads,      setLeads]      = useState([]);
  const [deals,      setDeals]      = useState([]);
  const [tasks,      setTasks]      = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);

  // ── Lead Detail (side panel) ──
  const [selectedLead, setSelectedLead]   = useState(null);
  const [leadDetail,   setLeadDetail]     = useState(null);
  const [detailTab,    setDetailTab]      = useState('activities');
  const [activityForm, setActivityForm]   = useState({ type: 'Note', title: '', description: '' });
  const [savingActivity, setSavingActivity] = useState(false);

  // ── Lead Modals ──
  const [showLeadModal,  setShowLeadModal]  = useState(false);
  const [editLead,       setEditLead]       = useState(null);
  const [leadForm,       setLeadForm]       = useState({ name:'', email:'', phone:'', company:'', source:'Manual', status:'New', score:50, notes:'' });
  const [savingLead,     setSavingLead]     = useState(false);
  const [leadSearch,     setLeadSearch]     = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('All');

  // ── Lead Delete ──
  const [showLeadDeleteModal, setShowLeadDeleteModal] = useState(false);
  const [leadToDelete,        setLeadToDelete]        = useState(null);
  const [deletingLead,        setDeletingLead]        = useState(false);

  // ── Deal Modals ──
  const [showDealModal,  setShowDealModal]  = useState(false);
  const [editDeal,       setEditDeal]       = useState(null);
  const [dealForm,       setDealForm]       = useState({ lead_id:'', title:'', value:'', stage:'Prospecting', expected_close_date:'', probability:10, notes:'' });
  const [savingDeal,     setSavingDeal]     = useState(false);

  // ── Deal Delete ──
  const [showDealDeleteModal, setShowDealDeleteModal] = useState(false);
  const [dealToDelete,        setDealToDelete]        = useState(null);
  const [deletingDeal,        setDeletingDeal]        = useState(false);

  // ── Task Modals ──
  const [showTaskModal,  setShowTaskModal]  = useState(false);
  const [editTask,       setEditTask]       = useState(null);
  const [taskForm,       setTaskForm]       = useState({ lead_id:'', title:'', description:'', due_date:'', priority:'Medium', status:'Pending' });
  const [savingTask,     setSavingTask]     = useState(false);
  const [taskStatusFilter, setTaskStatusFilter] = useState('All');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('All');
  const [taskSearch, setTaskSearch] = useState('');

  // ── Task Delete ──
  const [showTaskDeleteModal, setShowTaskDeleteModal] = useState(false);
  const [taskToDelete,        setTaskToDelete]        = useState(null);
  const [deletingTask,        setDeletingTask]        = useState(false);

  // ── Fetch data ──────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, dealsRes, tasksRes, statsRes] = await Promise.all([
        getCRMLeads(), getCRMDeals(), getCRMTasks(), getCRMStats(),
      ]);
      setLeads(leadsRes.data);
      setDeals(dealsRes.data);
      setTasks(tasksRes.data);
      setStats(statsRes.data);
    } catch (err) {
      showErrorToast('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchLeadDetail = async (id) => {
    try {
      const { data } = await getCRMLead(id);
      setLeadDetail(data);
    } catch { /* keep */ }
  };

  // ── Open Lead Detail ──
  const openLeadDetail = (lead) => {
    setSelectedLead(lead);
    setDetailTab('activities');
    setActivityForm({ type: 'Note', title: '', description: '' });
    fetchLeadDetail(lead.id);
  };

  // ── Add Activity ──
  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.title.trim()) { showErrorToast('Activity title required'); return; }
    setSavingActivity(true);
    try {
      await createCRMActivity({ ...activityForm, lead_id: selectedLead.id });
      setActivityForm({ type: 'Note', title: '', description: '' });
      showSuccessToast('Activity logged!');
      fetchLeadDetail(selectedLead.id);
    } catch { showErrorToast('Failed to log activity'); }
    finally { setSavingActivity(false); }
  };

  // ══ LEADS CRUD ═══════════════════════════════════════

  const openAddLead = () => {
    setEditLead(null);
    setLeadForm({ name:'', email:'', phone:'', company:'', source:'Manual', status:'New', score:50, notes:'' });
    setShowLeadModal(true);
  };

  const openEditLead = (lead, e) => {
    e.stopPropagation();
    setEditLead(lead);
    setLeadForm({
      name: lead.name, email: lead.email || '', phone: lead.phone || '',
      company: lead.company || '', source: lead.source, status: lead.status,
      score: lead.score, notes: lead.notes || '',
    });
    setShowLeadModal(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name.trim()) { showErrorToast('Name is required'); return; }
    setSavingLead(true);
    const toastId = showLoadingToast(editLead ? 'Updating lead...' : 'Adding lead...');
    try {
      if (editLead) {
        await updateCRMLead(editLead.id, leadForm);
        updateLoadingToastSuccess(toastId, 'Lead updated!');
      } else {
        await createCRMLead(leadForm);
        updateLoadingToastSuccess(toastId, 'Lead added!');
      }
      setShowLeadModal(false);
      fetchAll();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed');
    } finally { setSavingLead(false); }
  };

  const handleDeleteLead = async () => {
    if (!leadToDelete) return;
    setDeletingLead(true);
    const toastId = showLoadingToast('Deleting lead...');
    try {
      await deleteCRMLead(leadToDelete.id);
      updateLoadingToastSuccess(toastId, 'Lead deleted!');
      setShowLeadDeleteModal(false);
      setLeadToDelete(null);
      if (selectedLead?.id === leadToDelete.id) setSelectedLead(null);
      fetchAll();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed');
    } finally { setDeletingLead(false); }
  };

  // ══ DEALS CRUD ═══════════════════════════════════════

  const openAddDeal = () => {
    setEditDeal(null);
    setDealForm({ lead_id:'', title:'', value:'', stage:'Prospecting', expected_close_date:'', probability:10, notes:'' });
    setShowDealModal(true);
  };

  const openEditDeal = (deal, e) => {
    if (e) e.stopPropagation();
    setEditDeal(deal);
    setDealForm({
      lead_id: deal.lead_id ? String(deal.lead_id) : '',
      title: deal.title,
      value: deal.value,
      stage: deal.stage,
      expected_close_date: deal.expected_close_date ? deal.expected_close_date.split('T')[0] : '',
      probability: deal.probability,
      notes: deal.notes || '',
    });
    setShowDealModal(true);
  };

  const handleDealSubmit = async (e) => {
    e.preventDefault();
    if (!dealForm.title.trim()) { showErrorToast('Deal title required'); return; }
    setSavingDeal(true);
    const toastId = showLoadingToast(editDeal ? 'Updating deal...' : 'Adding deal...');
    try {
      if (editDeal) {
        await updateCRMDeal(editDeal.id, dealForm);
        updateLoadingToastSuccess(toastId, 'Deal updated!');
      } else {
        await createCRMDeal(dealForm);
        updateLoadingToastSuccess(toastId, 'Deal added!');
      }
      setShowDealModal(false);
      fetchAll();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed');
    } finally { setSavingDeal(false); }
  };

  const handleDeleteDeal = async () => {
    if (!dealToDelete) return;
    setDeletingDeal(true);
    const toastId = showLoadingToast('Deleting deal...');
    try {
      await deleteCRMDeal(dealToDelete.id);
      updateLoadingToastSuccess(toastId, 'Deal deleted!');
      setShowDealDeleteModal(false);
      setDealToDelete(null);
      fetchAll();
    } catch (err) {
      updateLoadingToastError(toastId, 'Failed to delete');
    } finally { setDeletingDeal(false); }
  };

  // Quick stage update from pipeline
  const handleQuickStageChange = async (deal, newStage) => {
    try {
      await updateCRMDeal(deal.id, {
        ...deal,
        lead_id: deal.lead_id ? String(deal.lead_id) : '',
        stage: newStage,
        probability: STAGE_PROBABILITY[newStage],
        expected_close_date: deal.expected_close_date ? deal.expected_close_date.split('T')[0] : '',
      });
      fetchAll();
    } catch { showErrorToast('Failed to update stage'); }
  };

  // ══ TASKS CRUD ═══════════════════════════════════════

  const openAddTask = () => {
    setEditTask(null);
    setTaskForm({ lead_id:'', title:'', description:'', due_date:'', priority:'Medium', status:'Pending' });
    setShowTaskModal(true);
  };

  const openEditTask = (task, e) => {
    if (e) e.stopPropagation();
    setEditTask(task);
    setTaskForm({
      lead_id: task.lead_id ? String(task.lead_id) : '',
      title: task.title,
      description: task.description || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      priority: task.priority,
      status: task.status,
    });
    setShowTaskModal(true);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) { showErrorToast('Task title required'); return; }
    setSavingTask(true);
    const toastId = showLoadingToast(editTask ? 'Updating task...' : 'Adding task...');
    try {
      if (editTask) {
        await updateCRMTask(editTask.id, taskForm);
        updateLoadingToastSuccess(toastId, 'Task updated!');
      } else {
        await createCRMTask(taskForm);
        updateLoadingToastSuccess(toastId, 'Task created!');
      }
      setShowTaskModal(false);
      fetchAll();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed');
    } finally { setSavingTask(false); }
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'Completed' ? 'Pending' : 'Completed';
    try {
      await updateCRMTask(task.id, { ...task, lead_id: task.lead_id ? String(task.lead_id) : '', due_date: task.due_date ? task.due_date.split('T')[0] : '', status: newStatus });
      fetchAll();
    } catch { showErrorToast('Failed to update task'); }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    setDeletingTask(true);
    const toastId = showLoadingToast('Deleting task...');
    try {
      await deleteCRMTask(taskToDelete.id);
      updateLoadingToastSuccess(toastId, 'Task deleted!');
      setShowTaskDeleteModal(false);
      setTaskToDelete(null);
      fetchAll();
    } catch (err) {
      updateLoadingToastError(toastId, 'Failed to delete');
    } finally { setDeletingTask(false); }
  };

  // ── Filtered Data ──────────────────────────────────
  const filteredLeads = leads.filter((l) => {
    const matchStatus = leadStatusFilter === 'All' || l.status === leadStatusFilter;
    const matchSearch = !leadSearch.trim() ||
      l.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
      (l.company || '').toLowerCase().includes(leadSearch.toLowerCase()) ||
      (l.email || '').toLowerCase().includes(leadSearch.toLowerCase());
    return matchStatus && matchSearch;
  });

  const filteredTasks = tasks.filter((t) => {
    const matchStatus   = taskStatusFilter === 'All' || t.status === taskStatusFilter;
    const matchPriority = taskPriorityFilter === 'All' || t.priority === taskPriorityFilter;
    const matchSearch   = !taskSearch.trim() || t.title.toLowerCase().includes(taskSearch.toLowerCase());
    return matchStatus && matchPriority && matchSearch;
  });

  // Pipeline grouped by stage
  const dealsByStage = DEAL_STAGES.reduce((acc, stage) => {
    acc[stage] = deals.filter((d) => d.stage === stage);
    return acc;
  }, {});

  const pipelineTotal = deals.reduce((s, d) => s + parseFloat(d.value || 0), 0);

  // ══════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════
  return (
    <div className="crm-page">

      {/* ── Tab Bar ────────────────────────────────── */}
      <nav className="crm-tabs">
        {[
          { key: 'leads',    label: 'Leads',    Icon: FiUser,      count: leads.length },
          { key: 'pipeline', label: 'Pipeline', Icon: FiLayers,    count: deals.length },
          { key: 'tasks',    label: 'Tasks',    Icon: FiList,      count: tasks.filter(t => t.status !== 'Completed').length },
          { key: 'analytics',label: 'Analytics',Icon: FiBarChart2, count: null },
        ].map(({ key, label, Icon, count }) => (
          <button
            key={key}
            className={`crm-tab-btn ${activeTab === key ? 'crm-tab-active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={15} />
            {label}
            {count !== null && count > 0 && <span className="crm-tab-count">{count}</span>}
          </button>
        ))}
      </nav>

      {/* ── Main Content ────────────────────────────── */}
      <main className="crm-content">
        {loading ? (
          <div className="crm-empty">
            <div className="crm-empty-icon" style={{ animation: 'crm-fade-in 0.5s ease infinite alternate' }}>
              <FiActivity size={28} />
            </div>
            <h4>Loading CRM data...</h4>
            <p>Fetching your leads, deals, and tasks</p>
          </div>
        ) : (
          <>
            {/* ══════════ LEADS TAB ══════════ */}
            {activeTab === 'leads' && (
              <>
                {/* Filter chips */}
                <div className="crm-filter-chips" style={{ marginBottom: 14 }}>
                  {['All', ...LEAD_STATUSES].map((s) => {
                    const cnt = s === 'All' ? leads.length : leads.filter(l => l.status === s).length;
                    return (
                      <button
                        key={s}
                        className={`crm-chip ${leadStatusFilter === s ? 'active' : ''}`}
                        onClick={() => setLeadStatusFilter(s)}
                      >
                        {s}
                        <span className="crm-chip-count">{cnt}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Toolbar */}
                <div className="crm-toolbar">
                  <div className="crm-search-wrap">
                    <FiSearch size={14} className="crm-search-icon" />
                    <input
                      className="crm-search-input"
                      placeholder="Search leads by name, company, or email..."
                      value={leadSearch}
                      onChange={(e) => setLeadSearch(e.target.value)}
                    />
                    {leadSearch && <button className="crm-icon-btn" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} onClick={() => setLeadSearch('')}><FiX size={13} /></button>}
                  </div>
                  <button className="crm-btn-primary" onClick={openAddLead}>
                    <FiPlus size={15} /> Add Lead
                  </button>
                </div>

                {filteredLeads.length === 0 ? (
                  <EmptyState
                    icon={FiUser} title="No leads found"
                    subtitle={leadSearch ? `No leads match "${leadSearch}"` : 'Start adding leads to track your sales pipeline.'}
                    onAction={openAddLead} actionLabel="Add First Lead"
                  />
                ) : (
                  <div className="crm-table-card">
                    <table className="crm-table">
                      <thead>
                        <tr>
                          <th>Lead</th>
                          <th>Company</th>
                          <th>Contact</th>
                          <th>Source</th>
                          <th>Status</th>
                          <th>Score</th>
                          <th>Activity</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeads.map((lead) => (
                          <tr key={lead.id} onClick={() => openLeadDetail(lead)}>
                            <td>
                              <div className="crm-lead-cell">
                                <div className="crm-avatar" style={{ background: getAvatarColor(lead.name) }}>
                                  {lead.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div className="crm-lead-name">{lead.name}</div>
                                  <div className="crm-lead-email">{lead.email || '—'}</div>
                                </div>
                              </div>
                            </td>
                            <td><span className="crm-lead-company">{lead.company || '—'}</span></td>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                                {lead.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FiMail size={11} /> {lead.email}</span>}
                                {lead.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FiPhone size={11} /> {lead.phone}</span>}
                                {!lead.email && !lead.phone && <span>—</span>}
                              </div>
                            </td>
                            <td><span className="crm-badge">{lead.source}</span></td>
                            <td><Badge text={lead.status} badgeMap={STATUS_BADGE} /></td>
                            <td><ScoreBadge score={lead.score} /></td>
                            <td>
                              <div style={{ display: 'flex', gap: 6 }}>
                                {parseInt(lead.activity_count) > 0 && <span className="crm-badge" style={{ fontSize: 11 }}><FiActivity size={10} /> {lead.activity_count}</span>}
                                {parseInt(lead.task_count) > 0 && <span className="crm-badge" style={{ fontSize: 11 }}><FiCheckCircle size={10} /> {lead.task_count}</span>}
                                {parseInt(lead.deal_count) > 0 && <span className="crm-badge" style={{ fontSize: 11 }}><FiTarget size={10} /> {lead.deal_count}</span>}
                                {parseInt(lead.activity_count) + parseInt(lead.task_count) + parseInt(lead.deal_count) === 0 && <span style={{ color: 'var(--crm-text-subtle)' }}>—</span>}
                              </div>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="crm-row-actions">
                                <button className="crm-icon-btn crm-view" title="View Details" onClick={(e) => { e.stopPropagation(); openLeadDetail(lead); }}>
                                  <FiEye size={13} />
                                </button>
                                <button className="crm-icon-btn" title="Edit" onClick={(e) => openEditLead(lead, e)}>
                                  <FiEdit2 size={13} />
                                </button>
                                <button className="crm-icon-btn crm-danger" title="Delete" onClick={(e) => { e.stopPropagation(); setLeadToDelete(lead); setShowLeadDeleteModal(true); }}>
                                  <FiTrash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* ══════════ PIPELINE TAB ══════════ */}
            {activeTab === 'pipeline' && (
              <div className="crm-pipeline-wrap">
                <div className="crm-pipeline-header">
                  <div className="crm-pipeline-summary">
                    <span>Total pipeline: <strong>{fmtINR(pipelineTotal)}</strong></span>
                    <span>{deals.length} deal{deals.length !== 1 ? 's' : ''}</span>
                  </div>
                  <button className="crm-btn-primary" onClick={openAddDeal}>
                    <FiPlus size={15} /> Add Deal
                  </button>
                </div>

                <div className="crm-kanban">
                  {DEAL_STAGES.map((stage) => {
                    const stageDeals = dealsByStage[stage];
                    const stageTotal = stageDeals.reduce((s, d) => s + parseFloat(d.value || 0), 0);
                    const stageIdx = DEAL_STAGES.indexOf(stage);
                    return (
                      <div key={stage} className="crm-kanban-col" data-stage={stage}>
                        <div className="crm-kanban-col-header">
                          <div className="crm-kanban-col-title-row">
                            <span className="crm-kanban-col-title">{stage}</span>
                            <span className="crm-kanban-col-count">{stageDeals.length}</span>
                          </div>
                          {stageTotal > 0 && <div className="crm-kanban-col-total">{fmtINR(stageTotal)}</div>}
                        </div>

                        <div className="crm-kanban-col-body">
                          {stageDeals.length === 0 ? (
                            <div className="crm-empty" style={{ padding: '30px 10px' }}>
                              <p style={{ fontSize: 12, margin: 0 }}>No deals</p>
                            </div>
                          ) : (
                            stageDeals.map((deal) => (
                              <div key={deal.id} className="crm-deal-card">
                                <div className="crm-deal-title">{deal.title}</div>

                                {deal.lead_name && (
                                  <div className="crm-deal-lead">
                                    <FiUser size={10} /> {deal.lead_name}
                                    {deal.lead_company && ` · ${deal.lead_company}`}
                                  </div>
                                )}

                                <div className="crm-deal-meta">
                                  <span className="crm-deal-value">
                                    <MdCurrencyRupee size={13} />{parseFloat(deal.value || 0).toLocaleString('en-IN')}
                                  </span>
                                  <span className="crm-deal-probability">{deal.probability}%</span>
                                </div>

                                {deal.expected_close_date && (
                                  <div className="crm-deal-lead" style={{ marginBottom: 0, marginTop: 6 }}>
                                    <FiCalendar size={10} /> {fmtDate(deal.expected_close_date)}
                                  </div>
                                )}

                                {/* Actions */}
                                <div className="crm-deal-actions">
                                  {stageIdx > 0 && (
                                    <button className="crm-deal-advance-btn" onClick={() => handleQuickStageChange(deal, DEAL_STAGES[stageIdx - 1])}>
                                      <FiChevronLeft size={12} /> Prev
                                    </button>
                                  )}
                                  <button className="crm-icon-btn" onClick={(e) => openEditDeal(deal, e)}><FiEdit2 size={11} /></button>
                                  <button className="crm-icon-btn crm-danger" onClick={(e) => { e.stopPropagation(); setDealToDelete(deal); setShowDealDeleteModal(true); }}><FiTrash2 size={11} /></button>
                                  {stageIdx < DEAL_STAGES.length - 1 && (
                                    <button className="crm-deal-advance-btn" onClick={() => handleQuickStageChange(deal, DEAL_STAGES[stageIdx + 1])}>
                                      Next <FiChevronRight size={12} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ══════════ TASKS TAB ══════════ */}
            {activeTab === 'tasks' && (
              <>
                <div className="crm-toolbar">
                  <div className="crm-search-wrap">
                    <FiSearch size={14} className="crm-search-icon" />
                    <input
                      className="crm-search-input"
                      placeholder="Search tasks..."
                      value={taskSearch}
                      onChange={(e) => setTaskSearch(e.target.value)}
                    />
                  </div>
                  <select className="crm-form-select" style={{ width: 'auto', minWidth: 130 }} value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)}>
                    <option value="All">All Status</option>
                    {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select className="crm-form-select" style={{ width: 'auto', minWidth: 130 }} value={taskPriorityFilter} onChange={(e) => setTaskPriorityFilter(e.target.value)}>
                    <option value="All">All Priority</option>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button className="crm-btn-primary" onClick={openAddTask}>
                    <FiPlus size={15} /> Add Task
                  </button>
                </div>

                {/* Task summary KPI chips */}
                <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: 18 }}>
                  {[
                    { label: 'Total',       val: tasks.length,                                     accent: 'primary', icon: FiList },
                    { label: 'Pending',      val: tasks.filter(t => t.status === 'Pending').length, accent: 'warning', icon: FiClock },
                    { label: 'In Progress',  val: tasks.filter(t => t.status === 'In Progress').length, accent: 'info', icon: FiActivity },
                    { label: 'Completed',    val: tasks.filter(t => t.status === 'Completed').length, accent: 'success', icon: FiCheckCircle },
                    { label: 'Overdue',      val: tasks.filter(t => isOverdue(t.due_date, t.status)).length, accent: 'danger', icon: FiAlertCircle },
                  ].map(({ label, val, accent, icon: Icon }) => (
                    <div key={label} className="crm-kpi-card" data-accent={accent} style={{ padding: 14 }}>
                      <div className="crm-kpi-head" style={{ marginBottom: 8 }}>
                        <div className="crm-kpi-icon-wrap" style={{ width: 30, height: 30 }}><Icon size={14} /></div>
                      </div>
                      <div className="crm-kpi-value" style={{ fontSize: 22 }}>{val}</div>
                      <div className="crm-kpi-label">{label}</div>
                    </div>
                  ))}
                </div>

                {filteredTasks.length === 0 ? (
                  <EmptyState
                    icon={FiCheckCircle} title="No tasks found"
                    subtitle="Schedule calls, meetings, and follow-ups to stay on top of your leads."
                    onAction={openAddTask} actionLabel="Create First Task"
                  />
                ) : (
                  <div className="crm-task-list">
                    {filteredTasks.map((task) => {
                      const overdue = isOverdue(task.due_date, task.status);
                      const isDone = task.status === 'Completed';
                      return (
                        <div key={task.id} className="crm-task-item">
                          <button
                            className={`crm-task-checkbox ${isDone ? 'checked' : ''}`}
                            onClick={() => handleToggleTask(task)}
                            title={isDone ? 'Mark as pending' : 'Mark as complete'}
                          >
                            {isDone && <FiCheck size={12} />}
                          </button>

                          <div className="crm-task-content">
                            <div className={`crm-task-title ${isDone ? 'done' : ''}`}>{task.title}</div>
                            {task.description && <p style={{ fontSize: 12, color: 'var(--crm-text-muted)', margin: '3px 0 0' }}>{task.description}</p>}
                            <div className="crm-task-sub">
                              {task.lead_name && <span className="crm-task-sub-item"><FiUser size={11} /> {task.lead_name}</span>}
                              {task.due_date && (
                                <span className={`crm-task-sub-item crm-task-due ${overdue ? 'overdue' : ''}`}>
                                  {overdue ? <FiAlertCircle size={11} /> : <FiCalendar size={11} />}
                                  {overdue ? 'Overdue: ' : ''}{fmtDate(task.due_date)}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="crm-task-right">
                            <Badge text={task.priority} badgeMap={PRIORITY_BADGE} />
                            <Badge text={task.status} badgeMap={TASK_STATUS_BADGE} />
                            <button className="crm-icon-btn" onClick={(e) => openEditTask(task, e)}><FiEdit2 size={13} /></button>
                            <button className="crm-icon-btn crm-danger" onClick={() => { setTaskToDelete(task); setShowTaskDeleteModal(true); }}><FiTrash2 size={13} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ══════════ ANALYTICS TAB ══════════ */}
            {activeTab === 'analytics' && stats && (
              <>
                {/* KPI Cards */}
                <div className="crm-kpi-grid">
                  {[
                    { label: 'Total Leads',    value: stats.leads.total_leads || 0,                                    icon: FiUser,          accent: 'primary' },
                    { label: 'Converted',      value: stats.leads.converted_leads || 0,                                icon: FiCheckCircle,   accent: 'success' },
                    { label: 'Pipeline Value', value: fmtINR(stats.deals.total_pipeline_value || 0),                   icon: MdCurrencyRupee, accent: 'accent',  raw: true },
                    { label: 'Won Deals',      value: stats.deals.won_deals || 0,                                      icon: FiTarget,        accent: 'warning' },
                    { label: 'Win Rate',       value: stats.deals.total_deals > 0 ? `${Math.round((stats.deals.won_deals / stats.deals.total_deals) * 100)}%` : '0%', icon: FiTrendingUp, accent: 'info', raw: true },
                    { label: 'Avg Lead Score', value: stats.leads.avg_score || 0,                                      icon: FiBarChart2,     accent: 'accent' },
                    { label: 'Open Tasks',     value: stats.tasks.pending_tasks || 0,                                  icon: FiClock,         accent: 'danger' },
                    { label: 'Won Revenue',    value: fmtINR(stats.deals.won_value || 0),                              icon: MdCurrencyRupee, accent: 'success', raw: true },
                  ].map(({ label, value, icon: Icon, accent, raw }) => (
                    <div key={label} className="crm-kpi-card" data-accent={accent}>
                      <div className="crm-kpi-head">
                        <div className="crm-kpi-icon-wrap"><Icon size={18} /></div>
                        <span className="crm-kpi-label">{label}</span>
                      </div>
                      <div className="crm-kpi-value">{raw ? value : value.toLocaleString()}</div>
                    </div>
                  ))}
                </div>

                <div className="crm-charts-grid">
                  {/* Lead Status Funnel */}
                  <div className="crm-chart-card">
                    <div className="crm-chart-header">
                      <div>
                        <div className="crm-chart-title">Lead Status Funnel</div>
                        <div className="crm-chart-sub">Distribution of leads by status</div>
                      </div>
                    </div>
                    <div className="crm-bar-rows">
                      {[
                        { status: 'New',       count: stats.leads.new_leads || 0,       color: 'info' },
                        { status: 'Contacted', count: stats.leads.contacted_leads || 0,  color: 'accent' },
                        { status: 'Qualified', count: stats.leads.qualified_leads || 0,  color: 'warning' },
                        { status: 'Converted', count: stats.leads.converted_leads || 0,  color: 'success' },
                        { status: 'Lost',      count: stats.leads.lost_leads || 0,       color: 'danger' },
                      ].map(({ status, count, color }) => {
                        const total = parseInt(stats.leads.total_leads) || 1;
                        const pct = Math.round((count / total) * 100);
                        return (
                          <div key={status} className="crm-bar-row">
                            <span className="crm-bar-label">{status}</span>
                            <div className="crm-bar-track">
                              <div className="crm-bar-fill" data-color={color} style={{ width: `${Math.max(pct, 2)}%` }} />
                            </div>
                            <span className="crm-bar-value">{count} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Lead Sources Donut */}
                  <div className="crm-chart-card">
                    <div className="crm-chart-header">
                      <div>
                        <div className="crm-chart-title">Lead Sources</div>
                        <div className="crm-chart-sub">Where your leads come from</div>
                      </div>
                    </div>
                    {stats.sources.length === 0 ? (
                      <div className="crm-empty" style={{ padding: 30 }}><p style={{ margin: 0 }}>No data yet</p></div>
                    ) : (
                      <DonutChart
                        total={parseInt(stats.leads.total_leads) || 0}
                        label="Leads"
                        segments={stats.sources.map(({ source, count }, i) => ({
                          value: count,
                          color: ['#0178BD', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#0891b2'][i % 6],
                          label: source,
                        }))}
                      />
                    )}
                  </div>

                  {/* Pipeline by Stage */}
                  <div className="crm-chart-card">
                    <div className="crm-chart-header">
                      <div>
                        <div className="crm-chart-title">Pipeline by Stage</div>
                        <div className="crm-chart-sub">Deals across your pipeline stages</div>
                      </div>
                    </div>
                    {stats.stages.length === 0 ? (
                      <div className="crm-empty" style={{ padding: 30 }}><p style={{ margin: 0 }}>No deals yet</p></div>
                    ) : (
                      <div className="crm-bar-rows">
                        {stats.stages.map(({ stage, count, value }, i) => {
                          const maxVal = Math.max(...stats.stages.map(s => parseFloat(s.value || 0)));
                          const pct = maxVal > 0 ? Math.round((parseFloat(value || 0) / maxVal) * 100) : 0;
                          const colors = ['muted', 'info', 'accent', 'warning', 'success', 'danger'];
                          return (
                            <div key={stage} className="crm-bar-row">
                              <span className="crm-bar-label">{stage}</span>
                              <div className="crm-bar-track">
                                <div className="crm-bar-fill" data-color={colors[i % 6]} style={{ width: `${Math.max(pct, 2)}%` }} />
                              </div>
                              <span className="crm-bar-value">{fmtINR(value)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Deal Stage Donut */}
                  <div className="crm-chart-card">
                    <div className="crm-chart-header">
                      <div>
                        <div className="crm-chart-title">Deals Distribution</div>
                        <div className="crm-chart-sub">Number of deals per stage</div>
                      </div>
                    </div>
                    {stats.stages.length === 0 ? (
                      <div className="crm-empty" style={{ padding: 30 }}><p style={{ margin: 0 }}>No deals yet</p></div>
                    ) : (
                      <DonutChart
                        total={deals.length}
                        label="Deals"
                        segments={stats.stages.map(({ stage, count }, i) => ({
                          value: count,
                          color: ['#94a3b8', '#0178BD', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444'][i % 6],
                          label: stage,
                        }))}
                      />
                    )}
                  </div>
                </div>

                {/* Recent Leads */}
                {stats.recentLeads?.length > 0 && (
                  <div className="crm-chart-card">
                    <div className="crm-chart-header">
                      <div>
                        <div className="crm-chart-title">Recent Leads</div>
                        <div className="crm-chart-sub">Latest leads added to the system</div>
                      </div>
                    </div>
                    <div className="crm-recent-leads">
                      {stats.recentLeads.map((lead) => (
                        <div key={lead.id} className="crm-recent-row">
                          <div className="crm-avatar" style={{ background: getAvatarColor(lead.name), width: 32, height: 32, fontSize: 12 }}>
                            {lead.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="crm-recent-info">
                            <div className="crm-recent-name">{lead.name}</div>
                            <div className="crm-recent-meta">{lead.company || 'No company'}</div>
                          </div>
                          <Badge text={lead.status} badgeMap={STATUS_BADGE} />
                          <ScoreBadge score={lead.score} />
                          <span style={{ fontSize: 12, color: 'var(--crm-text-subtle)', whiteSpace: 'nowrap' }}>{timeAgo(lead.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* ══════════════════════════════════════════════
          LEAD DETAIL SIDE PANEL
      ══════════════════════════════════════════════ */}
      {selectedLead && (
        <div className="crm-panel-overlay" onClick={() => setSelectedLead(null)}>
          <div className="crm-panel" onClick={(e) => e.stopPropagation()}>
            {/* Panel Header */}
            <div className="crm-panel-header">
              <div className="crm-panel-top">
                <div className="crm-panel-lead-head">
                  <div className="crm-panel-avatar" style={{ background: getAvatarColor(selectedLead.name) }}>
                    {selectedLead.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="crm-panel-lead-info">
                    <h3>{selectedLead.name}</h3>
                    {selectedLead.company && <p><FiBriefcase size={11} /> {selectedLead.company}</p>}
                  </div>
                </div>
                <button className="crm-panel-close" onClick={() => setSelectedLead(null)}><FiX size={16} /></button>
              </div>
              <div className="crm-panel-meta">
                <Badge text={selectedLead.status} badgeMap={STATUS_BADGE} />
                <span className="crm-badge">{selectedLead.source}</span>
                <ScoreBadge score={selectedLead.score} />
              </div>
            </div>

            {/* Contact info */}
            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--crm-border)', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: 'var(--crm-text-muted)' }}>
              {selectedLead.email && <a href={`mailto:${selectedLead.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--crm-primary-600)', textDecoration: 'none' }}><FiMail size={12} /> {selectedLead.email}</a>}
              {selectedLead.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FiPhone size={12} /> {selectedLead.phone}</span>}
            </div>

            {/* Panel Tabs */}
            <div className="crm-panel-tabs">
              {['activities', 'tasks', 'deals'].map((t) => (
                <button key={t} className={`crm-panel-tab ${detailTab === t ? 'active' : ''}`} onClick={() => setDetailTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                  {t === 'activities' && leadDetail?.activities?.length > 0 && <span className="crm-tab-count" style={{ fontSize: 10, minWidth: 16, height: 16, padding: '0 5px' }}>{leadDetail.activities.length}</span>}
                  {t === 'tasks'      && leadDetail?.tasks?.length > 0      && <span className="crm-tab-count" style={{ fontSize: 10, minWidth: 16, height: 16, padding: '0 5px' }}>{leadDetail.tasks.length}</span>}
                  {t === 'deals'      && leadDetail?.deals?.length > 0      && <span className="crm-tab-count" style={{ fontSize: 10, minWidth: 16, height: 16, padding: '0 5px' }}>{leadDetail.deals.length}</span>}
                </button>
              ))}
            </div>

            <div className="crm-panel-body">
              {/* Notes */}
              {leadDetail?.notes && (
                <div className="crm-panel-section">
                  <div className="crm-panel-section-title">Notes</div>
                  <p style={{ fontSize: 13, color: 'var(--crm-text-muted)', margin: 0, lineHeight: 1.6 }}>{leadDetail.notes}</p>
                </div>
              )}

              {/* Activities */}
              {detailTab === 'activities' && (
                <div className="crm-panel-section">
                  <div className="crm-panel-section-title">Log Activity</div>
                  <form className="crm-activity-form" onSubmit={handleAddActivity}>
                    <div className="crm-activity-form-row">
                      <select
                        className="crm-form-select"
                        style={{ width: 'auto', minWidth: 100 }}
                        value={activityForm.type}
                        onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}
                      >
                        {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <input
                        className="crm-form-input"
                        placeholder="Add a note or activity..."
                        value={activityForm.title}
                        onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                      />
                      <button type="submit" className="crm-btn-primary" disabled={savingActivity} style={{ padding: '8px 12px' }}>
                        {savingActivity ? '...' : <FiPlus size={14} />}
                      </button>
                    </div>
                  </form>

                  {leadDetail?.activities?.length > 0 && (
                    <div className="crm-timeline" style={{ marginTop: 18 }}>
                      {leadDetail.activities.map((act) => {
                        const Icon = ACTIVITY_ICON[act.type] || FiMessageSquare;
                        return (
                          <div key={act.id} className="crm-timeline-item">
                            <div className="crm-timeline-icon"><Icon size={11} /></div>
                            <div className="crm-timeline-content">
                              <div className="crm-timeline-title">{act.title}</div>
                              {act.description && <div className="crm-timeline-desc">{act.description}</div>}
                              <div className="crm-timeline-meta">
                                <span>{act.type}</span>
                                <span>{timeAgo(act.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {leadDetail?.activities?.length === 0 && (
                    <div className="crm-empty" style={{ padding: 20 }}><FiActivity size={20} /><p style={{ margin: '8px 0 0', fontSize: 12 }}>No activities yet</p></div>
                  )}
                </div>
              )}

              {/* Tasks */}
              {detailTab === 'tasks' && (
                <div className="crm-panel-section">
                  <div className="crm-panel-section-title">Linked Tasks</div>
                  {leadDetail?.tasks?.length === 0 && (
                    <div className="crm-empty" style={{ padding: 20 }}><FiCheckCircle size={20} /><p style={{ margin: '8px 0 0', fontSize: 12 }}>No tasks linked</p></div>
                  )}
                  {leadDetail?.tasks?.map((task) => (
                    <div key={task.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--crm-border-2)' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: task.status === 'Completed' ? 'var(--crm-text-subtle)' : 'var(--crm-text)', textDecoration: task.status === 'Completed' ? 'line-through' : 'none' }}>{task.title}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                          <Badge text={task.priority} badgeMap={PRIORITY_BADGE} />
                          {task.due_date && <span style={{ fontSize: 11, color: 'var(--crm-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><FiCalendar size={10} /> {fmtDate(task.due_date)}</span>}
                        </div>
                      </div>
                      <Badge text={task.status} badgeMap={TASK_STATUS_BADGE} />
                    </div>
                  ))}
                </div>
              )}

              {/* Deals */}
              {detailTab === 'deals' && (
                <div className="crm-panel-section">
                  <div className="crm-panel-section-title">Linked Deals</div>
                  {leadDetail?.deals?.length === 0 && (
                    <div className="crm-empty" style={{ padding: 20 }}><FiTarget size={20} /><p style={{ margin: '8px 0 0', fontSize: 12 }}>No deals linked</p></div>
                  )}
                  {leadDetail?.deals?.map((deal) => (
                    <div key={deal.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--crm-border-2)' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{deal.title}</div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 12, color: 'var(--crm-text-muted)', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, color: 'var(--crm-primary-700)' }}><MdCurrencyRupee size={11} />{parseFloat(deal.value || 0).toLocaleString('en-IN')}</span>
                          {deal.expected_close_date && <span><FiCalendar size={10} /> {fmtDate(deal.expected_close_date)}</span>}
                        </div>
                      </div>
                      <Badge text={deal.stage} badgeMap={{
                        Prospecting: '', Qualification: 'crm-badge-new', Proposal: 'crm-badge-contacted',
                        Negotiation: 'crm-badge-qualified', 'Closed Won': 'crm-badge-converted', 'Closed Lost': 'crm-badge-lost',
                      }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          ADD / EDIT LEAD MODAL
      ══════════════════════════════════════════════ */}
      {showLeadModal && (
        <div className="crm-modal-overlay" onClick={() => !savingLead && setShowLeadModal(false)}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-modal-header">
              <div className="crm-modal-title">
                <div className="crm-modal-icon"><FiUser size={16} /></div>
                {editLead ? 'Edit Lead' : 'Add New Lead'}
              </div>
              <button className="crm-panel-close" onClick={() => setShowLeadModal(false)}><FiX size={14} /></button>
            </div>
            <form onSubmit={handleLeadSubmit}>
              <div className="crm-modal-body">
                <div className="crm-form-grid">
                  <div className="crm-form-field">
                    <label className="crm-form-label">Name *</label>
                    <input className="crm-form-input" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} placeholder="Full name" required />
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Company</label>
                    <input className="crm-form-input" value={leadForm.company} onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })} placeholder="Company name" />
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Email</label>
                    <input className="crm-form-input" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="email@example.com" />
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Phone</label>
                    <input className="crm-form-input" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" />
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Source</label>
                    <select className="crm-form-select" value={leadForm.source} onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}>
                      {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Status</label>
                    <select className="crm-form-select" value={leadForm.status} onChange={(e) => setLeadForm({ ...leadForm, status: e.target.value })}>
                      {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="crm-form-field" style={{ marginTop: 14 }}>
                  <label className="crm-form-label">Lead Score: <span className="crm-form-label-sub">{leadForm.score}/100</span></label>
                  <input type="range" min="0" max="100" value={leadForm.score} onChange={(e) => setLeadForm({ ...leadForm, score: parseInt(e.target.value) })} style={{ width: '100%', accentColor: '#0178BD' }} />
                </div>
                <div className="crm-form-field" style={{ marginTop: 14 }}>
                  <label className="crm-form-label">Notes</label>
                  <textarea className="crm-form-textarea" value={leadForm.notes} onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} placeholder="Additional notes about this lead..." rows={3} />
                </div>
              </div>
              <div className="crm-modal-footer">
                <button type="button" className="crm-btn-secondary" onClick={() => setShowLeadModal(false)} disabled={savingLead}>Cancel</button>
                <button type="submit" className="crm-btn-primary" disabled={savingLead}>
                  {savingLead ? 'Saving...' : (editLead ? 'Update Lead' : 'Add Lead')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          ADD / EDIT DEAL MODAL
      ══════════════════════════════════════════════ */}
      {showDealModal && (
        <div className="crm-modal-overlay" onClick={() => !savingDeal && setShowDealModal(false)}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-modal-header">
              <div className="crm-modal-title">
                <div className="crm-modal-icon"><FiTarget size={16} /></div>
                {editDeal ? 'Edit Deal' : 'Add New Deal'}
              </div>
              <button className="crm-panel-close" onClick={() => setShowDealModal(false)}><FiX size={14} /></button>
            </div>
            <form onSubmit={handleDealSubmit}>
              <div className="crm-modal-body">
                <div className="crm-form-field" style={{ marginBottom: 14 }}>
                  <label className="crm-form-label">Deal Title *</label>
                  <input className="crm-form-input" value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} placeholder="e.g. Annual IT Consulting Contract" required />
                </div>
                <div className="crm-form-grid">
                  <div className="crm-form-field">
                    <label className="crm-form-label">Linked Lead</label>
                    <select className="crm-form-select" value={dealForm.lead_id} onChange={(e) => setDealForm({ ...dealForm, lead_id: e.target.value })}>
                      <option value="">— Select Lead —</option>
                      {leads.map(l => <option key={l.id} value={String(l.id)}>{l.name}{l.company ? ` (${l.company})` : ''}</option>)}
                    </select>
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Stage</label>
                    <select className="crm-form-select" value={dealForm.stage} onChange={(e) => setDealForm({ ...dealForm, stage: e.target.value, probability: STAGE_PROBABILITY[e.target.value] })}>
                      {DEAL_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Value (₹)</label>
                    <input className="crm-form-input" type="number" value={dealForm.value} onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })} placeholder="0" min="0" />
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Probability: <span className="crm-form-label-sub">{dealForm.probability}%</span></label>
                    <input type="range" min="0" max="100" value={dealForm.probability} onChange={(e) => setDealForm({ ...dealForm, probability: parseInt(e.target.value) })} style={{ width: '100%', accentColor: '#0178BD' }} />
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Expected Close Date</label>
                    <input className="crm-form-input" type="date" value={dealForm.expected_close_date} onChange={(e) => setDealForm({ ...dealForm, expected_close_date: e.target.value })} />
                  </div>
                </div>
                <div className="crm-form-field" style={{ marginTop: 14 }}>
                  <label className="crm-form-label">Notes</label>
                  <textarea className="crm-form-textarea" value={dealForm.notes} onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })} placeholder="Deal notes..." rows={2} />
                </div>
              </div>
              <div className="crm-modal-footer">
                <button type="button" className="crm-btn-secondary" onClick={() => setShowDealModal(false)} disabled={savingDeal}>Cancel</button>
                <button type="submit" className="crm-btn-primary" disabled={savingDeal}>
                  {savingDeal ? 'Saving...' : (editDeal ? 'Update Deal' : 'Add Deal')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          ADD / EDIT TASK MODAL
      ══════════════════════════════════════════════ */}
      {showTaskModal && (
        <div className="crm-modal-overlay" onClick={() => !savingTask && setShowTaskModal(false)}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-modal-header">
              <div className="crm-modal-title">
                <div className="crm-modal-icon"><FiCheckCircle size={16} /></div>
                {editTask ? 'Edit Task' : 'Add New Task'}
              </div>
              <button className="crm-panel-close" onClick={() => setShowTaskModal(false)}><FiX size={14} /></button>
            </div>
            <form onSubmit={handleTaskSubmit}>
              <div className="crm-modal-body">
                <div className="crm-form-field" style={{ marginBottom: 14 }}>
                  <label className="crm-form-label">Task Title *</label>
                  <input className="crm-form-input" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="e.g. Follow up call with client" required />
                </div>
                <div className="crm-form-grid">
                  <div className="crm-form-field">
                    <label className="crm-form-label">Linked Lead</label>
                    <select className="crm-form-select" value={taskForm.lead_id} onChange={(e) => setTaskForm({ ...taskForm, lead_id: e.target.value })}>
                      <option value="">— No Lead —</option>
                      {leads.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
                    </select>
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Priority</label>
                    <select className="crm-form-select" value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Status</label>
                    <select className="crm-form-select" value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}>
                      {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="crm-form-field">
                    <label className="crm-form-label">Due Date</label>
                    <input className="crm-form-input" type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} />
                  </div>
                </div>
                <div className="crm-form-field" style={{ marginTop: 14 }}>
                  <label className="crm-form-label">Description</label>
                  <textarea className="crm-form-textarea" value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} placeholder="Task details..." rows={2} />
                </div>
              </div>
              <div className="crm-modal-footer">
                <button type="button" className="crm-btn-secondary" onClick={() => setShowTaskModal(false)} disabled={savingTask}>Cancel</button>
                <button type="submit" className="crm-btn-primary" disabled={savingTask}>
                  {savingTask ? 'Saving...' : (editTask ? 'Update Task' : 'Create Task')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          DELETE MODALS
      ══════════════════════════════════════════════ */}
      {showLeadDeleteModal && (
        <div className="crm-modal-overlay" onClick={() => !deletingLead && setShowLeadDeleteModal(false)}>
          <div className="crm-modal crm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-modal-body" style={{ textAlign: 'center', padding: '30px 22px' }}>
              <div className="crm-delete-icon"><FiTrash2 size={24} /></div>
              <div className="crm-delete-title">Delete Lead</div>
              <div className="crm-delete-msg">Delete <strong>{leadToDelete?.name}</strong>? All related activities, tasks, and deals will also be removed.</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="crm-btn-secondary" onClick={() => setShowLeadDeleteModal(false)} disabled={deletingLead}>Cancel</button>
                <button className="crm-btn-danger" onClick={handleDeleteLead} disabled={deletingLead}>{deletingLead ? 'Deleting...' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDealDeleteModal && (
        <div className="crm-modal-overlay" onClick={() => !deletingDeal && setShowDealDeleteModal(false)}>
          <div className="crm-modal crm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-modal-body" style={{ textAlign: 'center', padding: '30px 22px' }}>
              <div className="crm-delete-icon"><FiTrash2 size={24} /></div>
              <div className="crm-delete-title">Delete Deal</div>
              <div className="crm-delete-msg">Delete <strong>{dealToDelete?.title}</strong>? This action cannot be undone.</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="crm-btn-secondary" onClick={() => setShowDealDeleteModal(false)} disabled={deletingDeal}>Cancel</button>
                <button className="crm-btn-danger" onClick={handleDeleteDeal} disabled={deletingDeal}>{deletingDeal ? 'Deleting...' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTaskDeleteModal && (
        <div className="crm-modal-overlay" onClick={() => !deletingTask && setShowTaskDeleteModal(false)}>
          <div className="crm-modal crm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crm-modal-body" style={{ textAlign: 'center', padding: '30px 22px' }}>
              <div className="crm-delete-icon"><FiTrash2 size={24} /></div>
              <div className="crm-delete-title">Delete Task</div>
              <div className="crm-delete-msg">Delete <strong>{taskToDelete?.title}</strong>? This action cannot be undone.</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="crm-btn-secondary" onClick={() => setShowTaskDeleteModal(false)} disabled={deletingTask}>Cancel</button>
                <button className="crm-btn-danger" onClick={handleDeleteTask} disabled={deletingTask}>{deletingTask ? 'Deleting...' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRM;
